import React, { useState, useEffect, useRef } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, setDoc } from "firebase/firestore";
import { db } from "./firebase";
import { defaultUIStrings } from "./uiStrings";

import "./TranslateWithAIModal.css";

export default function TranslateWithAIModal(props) {
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const showToast = (message, type = "info") => {
    setToast({ message, type });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => {
      setToast(null);
    }, 2500);
  };

  // Props destrutturati per compatibilità col resto del codice
  const {
    open,
    onClose,
    localeTag,
    onCompleted,
    dishes = [],
    categories = [],
    translationCredits = 0,
    mode = "bulk",
    dish = null
  } = props;

  
  const [loading, setLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");

  const [scope, setScope] = useState({
    dishes: true,
    categories: false,
    ui: false
  });


  const availableLanguages = [
    { code: "en", flag: "🇬🇧", label: "Inglese" },
    { code: "fr", flag: "🇫🇷", label: "Francese" },
    { code: "de", flag: "🇩🇪", label: "Tedesco" },
    { code: "es", flag: "🇪🇸", label: "Spagnolo" },
    { code: "pt", flag: "🇵🇹", label: "Portoghese" }
  ];

  const [selectedLanguages, setSelectedLanguages] = useState(["en"]);
  const languages = selectedLanguages;


  const aiModes = [
    {
      id: "simple",
      label: "Semplice",
      description: "Traduzione fedele"
    },
    {
      id: "cultural",
      label: "Adattamento culturale",
      description: "Adatta i testi alla cultura locale"
    },
    {
      id: "marketing",
      label: "Marketing",
      description: "Più descrittivo e coinvolgente"
    }
  ];
  const [selectedMode, setSelectedMode] = useState("simple");


  useEffect(() => {
    if (!open) return;
    setSelectedLanguages(["en"]);
    setSelectedMode("simple");
    if (mode === "single") {
      setScope({ dishes: true, categories: false, ui: false });
    } else {
      setScope({ dishes: true, categories: false, ui: false });
    }
  }, [open, mode]);


  // In modalità single dish, forza scope e nascondi sezione 1
  const isSingle = mode === "single";
  if (!open) return null;

  

  const startTranslation = async () => {
    if (!localeTag) {
      showToast("Locale non valido", "error");
      return;
    }

    if (!languages.length) {
      showToast("Seleziona almeno una lingua", "warning");
      return;
    }

    if (!isSingle && !scope.dishes && !scope.categories && !scope.ui) {
      showToast("Seleziona cosa tradurre", "warning");
      return;
    }

    let estimatedCalls = 0;

    if (isSingle) {
      estimatedCalls = languages.length;
    } else {
      const itemsToTranslate =
        (scope.dishes ? dishes.length : 0) +
        (scope.categories ? categories.length : 0) +
        (scope.ui ? Object.keys(defaultUIStrings).length : 0);

      estimatedCalls = itemsToTranslate * languages.length;
    }

    if (estimatedCalls > translationCredits) {
      showToast(
        `Crediti insufficienti: richiesti ${estimatedCalls}, disponibili ${translationCredits}`,
        "warning"
      );
      return;
    }

    setLoading(true);

    try {
      const functions = getFunctions();

      if (isSingle && dish) {
        const translateDishWithAI = httpsCallable(functions, "translateDishWithAI");

        setProgressMessage(`Piatto – ${languages.join(", ").toUpperCase()}`);

        await translateDishWithAI({
          localeTag,
          dishId: dish.id,
          selectedLangs: languages,
          aiMode: selectedMode
        });
      } else {
        if (scope.dishes && dishes.length > 0) {
          const translateDishWithAI = httpsCallable(functions, "translateDishWithAI");

          await Promise.all(
  dishes.map(async (currentDish, index) => {
    setProgressMessage(
      `Piatto ${index + 1} di ${dishes.length} – ${languages.join(", ").toUpperCase()}`
    );

    return translateDishWithAI({
      localeTag,
      dishId: currentDish.id,
      selectedLangs: languages,
      aiMode: selectedMode
    });
  })
);
        }

        if (scope.categories && categories.length > 0) {
          const translateCategoryWithAI = httpsCallable(functions, "translateCategoryWithAI");

          for (let i = 0; i < categories.length; i++) {
            const cat = categories[i];

            setProgressMessage(
              `Categoria ${i + 1} di ${categories.length} – ${languages.join(", ").toUpperCase()}`
            );

            await translateCategoryWithAI({
              localeTag,
              categoryId: cat.id,
              selectedLangs: languages,
              aiMode: selectedMode
            });

            
          }
        }

        if (scope.ui) {
          const translateTextWithAI = httpsCallable(functions, "translateTextWithAI");
          const totalStrings = Object.keys(defaultUIStrings).length;

          for (const lang of languages) {
            const translated = {};
            let currentString = 0;

            const entries = Object.entries(defaultUIStrings);

const chunkSize = 5;

for (let i = 0; i < entries.length; i += chunkSize) {
  const chunk = entries.slice(i, i + chunkSize);

  const results = await Promise.all(
    chunk.map(async ([key, italianText], index) => {
      const currentIndex = i + index + 1;

      setProgressMessage(
        `Testo UI ${currentIndex} di ${totalStrings} – ${lang.toUpperCase()}`
      );

      const result = await translateTextWithAI({
        text: italianText,
        from: "it",
        to: lang,
        mode: selectedMode
      });

      return {
        key,
        translated: result.data.translated
      };
    })
  );

  results.forEach((r) => {
    translated[r.key] = r.translated;
  });
}

            const uiTransRef = doc(db, "restaurants", localeTag, "ai", "uiTranslations");

            await setDoc(
              uiTransRef,
              { [lang]: translated },
              { merge: true }
            );
          }
        }
      }

      showToast(`Traduzione completata (${languages.length} lingue)`, "success");

      await onCompleted?.();
      onClose?.();
    } catch (err) {
      console.error(err);

      if (err?.message?.toLowerCase().includes("crediti")) {
        showToast("Crediti AI esauriti", "error");
      } else if (err?.code === "resource-exhausted") {
        showToast("Troppe richieste, riprova tra poco", "warning");
      } else {
        showToast("Errore durante la traduzione", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="me-modal-overlay">
      <div className="me-modal">


        <div className="me-modal-header">
          <h3 className="me-section-title">
            {isSingle && dish ? `🤖 Traduzione piatto: ${dish.nome}` : "✨ Traduzione AI"}
          </h3>
          <button className="me-btn me-btn-ghost me-btn-xs" onClick={onClose}>
            ❌
          </button>
        </div>

        {!isSingle && (
          <div className="me-modal-section">
            <div className="ai-section">
              <h3>1. Cosa vuoi tradurre</h3>
              <div
                className={`ai-scope-card ${scope.dishes ? "active" : ""}`}
                onClick={() =>
                  setScope((s) => ({ ...s, dishes: !s.dishes }))
                }
              >
                <span className="ai-scope-icon">🍽️</span>
                <div className="ai-scope-text">
                  <strong>Piatti</strong>
                  <small>Nome, descrizione, ingredienti e allergeni</small>
                </div>
              </div>
              <div
                className={`ai-scope-card ${scope.categories ? "active" : ""}`}
                onClick={() =>
                  setScope((s) => ({ ...s, categories: !s.categories }))
                }
              >
                <span className="ai-scope-icon">📂</span>
                <div className="ai-scope-text">
                  <strong>Categorie</strong>
                  <small>Titoli delle sezioni del menu</small>
                </div>
              </div>
              <div
                className={`ai-scope-card ${scope.ui ? "active" : ""}`}
                onClick={() =>
                  setScope((s) => ({ ...s, ui: !s.ui }))
                }
              >
                <span className="ai-scope-icon">🧩</span>
                <div className="ai-scope-text">
                  <strong>Testi UI</strong>
                  <small>Pulsanti, messaggi e interfaccia</small>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="me-modal-section">
          <div className="ai-section">
            <h3>2. Scegli la lingua</h3>
            <div className="ai-language-grid">
              {availableLanguages.map((lang) => (
                <div
                  key={lang.code}
                  className={`ai-language-card ${selectedLanguages.includes(lang.code) ? "active" : ""}`}
                  onClick={() =>
                    setSelectedLanguages((prev) =>
                      prev.includes(lang.code)
                        ? prev.filter((l) => l !== lang.code)
                        : [...prev, lang.code]
                    )
                  }
                >
                  <span className="flag">{lang.flag}</span>
                  <span className="label">{lang.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="me-modal-section">
          <div className="ai-section">
            <h3>3. Come deve tradurre l’AI</h3>
            {aiModes.map((mode) => (
              <div
                key={mode.id}
                className={`ai-mode-card ${selectedMode === mode.id ? "active" : ""}`}
                onClick={() => setSelectedMode(mode.id)}
              >
                <strong>{mode.label}</strong>
                <small>{mode.description}</small>
              </div>
            ))}
          </div>
        </div>

        <div className="me-modal-footer">
          <button className="me-action-pill big" onClick={onClose}>
            Annulla
          </button>

          <button
            className="me-action-pill primary big"
            disabled={loading}
            onClick={startTranslation}
          >
            {loading ? (progressMessage || "⏳ Traduzione in corso...") : "🤖 Avvia traduzione"}
          </button>
        </div>
      </div>
    </div>
    {toast && (
      <div className={`qr-toast ${toast.type}`}>
        {toast.message}
      </div>
    )}
  </>
);
}
